import type {
  Path,
  PathValue,
  UseFormReturn,
  UseFormSetError,
} from "react-hook-form";

import { nanoid } from "nanoid";

import { toast } from "@/components/toast/toast-queue.ts";
import { useEditRequestModals } from "@/features/request/edit/contexts/edit-request-modals.context.tsx";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate.tsx";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";
import authenticatedApi from "@/lib/api/client.ts";

import type { Asset, RequestFormData } from "../types.ts";
import type { LinkFormValues } from "./useLinkForm.tsx";

import { LINK_TYPE, MAX_FILES } from "../utils/uploadUtils.ts";

export function useLinkManagement<TFormValues extends RequestFormData>(
  field: Path<TFormValues>,
  assets: Asset[],
  methods: UseFormReturn<TFormValues>,
  setError: (error: null | string) => void,
  setLinkFormError: UseFormSetError<LinkFormValues>,
): {
  handleLinkAdd: (url: { link: string }) => void;
  handleLinkDelete: (attachmentId: number) => void;
} {
  const { isTemplate } = useIsTemplate();
  const { openUnlock } = useEditRequestModals();
  const { updateField } = useFieldUpdate();
  const handleLinkAdd = async (url: { link: string }) => {
    if (isTemplate) {
      openUnlock();

      return;
    }
    setError(null);
    if (assets.length >= MAX_FILES) {
      setError(`You can only upload a maximum of ${MAX_FILES} files.`);

      // Hack to set useLinkForm formState.isSubmitSuccessful to false and prevent it from resetting the form
      setLinkFormError("root", {
        type: "added-assets-limit-reached",
      });

      return;
    }

    try {
      const response = await authenticatedApi
        .url("/attachments/upload-link")
        .post({
          type: LINK_TYPE[field as keyof typeof LINK_TYPE],
          url: url.link,
        })
        .json<{ attachmentId: number }>();

      methods.setValue(field, [
        { attachmentId: response.attachmentId, id: nanoid(), url: url.link },
        ...assets,
      ] as PathValue<TFormValues, Path<TFormValues>>);

      if (field === "assets" || field === "inspirations") {
        void updateField(field, methods.getValues(field));
      }
    } catch {
      toast.add({
        description: "Failed to add link",
        timeout: 2500,
        title: "Error",
        type: "error",
      });
    }
  };

  const handleLinkDelete = async (attachmentId: number) => {
    setError(null);

    authenticatedApi.url(`/attachments/${attachmentId}`).delete();

    const filteredAssets = assets.filter(
      (asset) => asset.attachmentId !== attachmentId,
    );

    methods.setValue(
      field,
      filteredAssets as PathValue<TFormValues, Path<TFormValues>>,
    );

    toast.add({
      description: "The link has been successfully deleted.",
      timeout: 2000,
      title: "Link deleted",
      type: "success",
    });
  };

  return { handleLinkAdd, handleLinkDelete };
}
